import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#home', label: t('nav.home') },
    { href: '#about', label: t('nav.about') },
    { href: '#menu', label: t('nav.menu') },
    { href: '#gallery', label: t('nav.gallery') },
    { href: '#reservation', label: t('nav.reservation') },
    { href: '/review', label: t('nav.reviews'), isExternal: true },
  ];

  const languages = ['LV', 'EN', 'RU'];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav 
        data-testid="main-navigation"
        className={`nav-fixed py-4 px-6 md:px-12 transition-all duration-300 ${
          isScrolled ? 'nav-scrolled py-3' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <a 
            href="#home" 
            onClick={(e) => { e.preventDefault(); scrollToSection('#home'); }}
            className="flex items-center gap-3"
            data-testid="nav-logo"
          >
            <span className="font-['Playfair_Display'] text-xl md:text-2xl tracking-wide text-[#8B2942]">
              PAVILLON
            </span>
            <span className="font-['Playfair_Display'] text-xl md:text-2xl tracking-wide text-[#1A1A1D]">
              de ROZE
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              link.isExternal ? (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-sm tracking-widest uppercase text-[#1A1A1D]/70 hover:text-[#8B2942] transition-colors underline-animation"
                  data-testid={`nav-link-${link.href.replace('/', '')}`}
                >
                  {link.label}
                </a>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                  className="text-sm tracking-widest uppercase text-[#1A1A1D]/70 hover:text-[#8B2942] transition-colors underline-animation"
                  data-testid={`nav-link-${link.href.replace('#', '')}`}
                >
                  {link.label}
                </a>
              )
            ))}
          </div>

          {/* Language Switcher & Mobile Menu Button */}
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-1" data-testid="language-switcher">
              {languages.map((lang) => (
                <button
                  key={lang}
                  onClick={() => setLanguage(lang.toLowerCase())}
                  className={`lang-btn uppercase ${
                    language === lang.toLowerCase() ? 'active' : 'text-[#1A1A1D]/50'
                  }`}
                  data-testid={`lang-btn-${lang.toLowerCase()}`}
                >
                  {lang}
                </button>
              ))}
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden text-[#1A1A1D] p-2"
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute top-6 right-6 text-[#1A1A1D] p-2"
            data-testid="mobile-menu-close"
            aria-label="Close menu"
          >
            <X size={24} />
          </button>

          {navLinks.map((link, index) => (
            link.isExternal ? (
              <a
                key={link.href}
                href={link.href}
                className="font-['Playfair_Display'] text-2xl text-[#1A1A1D] hover:text-[#8B2942] transition-colors"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`mobile-nav-link-${link.href.replace('/', '')}`}
              >
                {link.label}
              </a>
            ) : (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                className="font-['Playfair_Display'] text-2xl text-[#1A1A1D] hover:text-[#8B2942] transition-colors"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`mobile-nav-link-${link.href.replace('#', '')}`}
              >
                {link.label}
              </a>
            )
          ))}

          <div className="flex items-center gap-4 mt-8">
            {languages.map((lang) => (
              <button
                key={lang}
                onClick={() => {
                  setLanguage(lang.toLowerCase());
                  setIsMobileMenuOpen(false);
                }}
                className={`lang-btn uppercase text-lg ${
                  language === lang.toLowerCase() ? 'active' : 'text-[#1A1A1D]/50'
                }`}
                data-testid={`mobile-lang-btn-${lang.toLowerCase()}`}
              >
                {lang}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
